<template>
  <div class="results-component-container">
    <page-header :breadcrumbs="breadcrumbs">
      <template #topbar>
        <h1>{{ title }}</h1>
        <div class="correct">
          <span v-if="shouldShowScenarioBox">{{ correctFlashcardStates }}/{{ allFlashcards.length }}</span>
          <span v-else>{{ percentCorrect }}%</span> Correct
        </div>
      </template>
    </page-header>
    <scenario-box
      v-if="shouldShowScenarioBox"
      :flashcard="allFlashcards[0]"
    ></scenario-box>
    <question-list
      :questions="processedQuestions"
    />
  </div>
</template>

<script>
import CategoriesMixin from '@/mixins/categories';
import GlobalMixin from '@/mixins/global';
import QuizMixin from '@/mixins/quizzes';
import BlueprintMixin from '@/mixins/blueprint';
import WuxSimulationSorting from '@/mixins/wux-simulation-sorting';
import QuestionProcessingMixin from '@/mixins/questionProcessing';

import PageHeader from '@components/singletons/pageHeader';
import QuestionList from '@components/progress/questions/QuestionList';
import { rateLimitingEnabled } from '@/utils';
import ScenarioBox from '@components/flashcards/scenario-box';

export default {
  components: { PageHeader, QuestionList, ScenarioBox },
  mixins: [CategoriesMixin, GlobalMixin, QuizMixin, BlueprintMixin, WuxSimulationSorting, QuestionProcessingMixin],
  computed: {
    allFlashcards() {
      if (this.isBlueprint) {
        return [...this.blueprintFlashcards];
      } else if (this.isSimulationSection) {
        return this.$store.getters['flashcards/flashcardList'].filter(f => f.categoryIds.includes(this.$route.params.sectionId));
      }
      return [];
    },
    breadcrumbs() {
      const breadcrumbs = [
        {
          name: this.category.name,
          route: {
            name: 'sims#performance',
            params: {
              simId: this.categoryId,
            },
          },
        },
      ];

      if (this.isBlueprint) {
        breadcrumbs.push({ name: this.blueprintGrouping.name });
      } else {
        breadcrumbs.push({ name: this.section.name });
      }

      return breadcrumbs;
    },
    categoryId() {
      return this.$route.params.categoryId;
    },
    category() {
      return this.categories[this.categoryId] || {};
    },
    isSimulationSection() {
      const sectionId = this.$route.params.sectionId;
      return !!sectionId;
    },
    correctFlashcardStates() {
      if (this.isBlueprint) {
        return this.getBlueprintFlashcardStates(this.parentCategory.id, this.blueprintId).filter(fs => fs && fs.correct).length;
      }
      return this.quizFlashcardStates(this.parentCategory).filter(fs => fs && fs.correct).length;
    },
    parentCategory() {
      if (this.isBlueprint) {
        return this.categories[this.categoryId] || {};
      }
      return this.categories[this.sectionId] || {};
    },
    percentCorrect() {
      let percent = (this.correctFlashcardStates / this.allFlashcards.length) * 100;
      return isFinite(percent) ? Math.round(percent) : 0;
    },
    sectionId() {
      return this.$route.params.sectionId;
    },
    section() {
      return this.categories[this.sectionId] || {};
    },
    shouldShowScenarioBox() {
      if (this.allFlashcards[0]?.assetAssociations?.length) {
        return this.allFlashcards[0].assetAssociations.some(assetAssociation =>
          assetAssociation?.displayType === 'scenario_box',
        );
      }
      return false;
    },
    states() {
      if (this.isBlueprint) {
        return this.getBlueprintFlashcardStates(this.categoryId) || [];
      }
      return this.quizFlashcardStates(this.section) || [];
    },
    title() {
      return this.parentCategory.name;
    },
    processedQuestions() {
      const sortedStates = this.getSortedStates();
      return sortedStates.map(state => {
        const question = this.getQuestion(state);
        return {
          flashcardId: state.flashcardId,
          route: this.getQuestionRoute(state, question),
          correct: state.correct,
          text: this.getQuestionText(state, question),
        };
      });
    },
  },
  created() {
    this.$analytics.events.reviewedQuiz(this.$store, this.parentCategory);
    this.$store.dispatch('flashcardStates/fetchStates', ['flashcards']);

    this.fetchFlashcards();
  },
  mounted() {
    this.fetchFlashcards();
  },
  methods: {
    fetchFlashcards() {
      if (this.isSimulationSection) {
        const params = {
          categoryId: this.sectionId,
          rateLimited: rateLimitingEnabled({
            store: this.$store,
            apptimize: this.$apptimize,
            analytics: this.$analytics,
          }),
        };

        this.$store.dispatch('flashcards/fetchFlashcards', params);
      } else if (this.allFlashcards.length < this.parentCategory?.counts?.total) {
        const params = {
          categoryId: this.parentCategory.id,
          rateLimited: rateLimitingEnabled({
            store: this.$store,
            apptimize: this.$apptimize,
            analytics: this.$analytics,
          }),
        };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }
    },
    getSortedStates() {
      if (this.states.length === 0) return [];

      let states = this.states.filter(s => this.getQuestion(s));

      return [...states].sort(this.flashcardStateComparator);
    },

    getQuestionRoute(state, question) {
      if (!question) return {};

      const isSingleSectionSim = this.$route.name === 'sims#results';

      if (isSingleSectionSim) {
        return {
          name: 'sims#results#question',
          params: {
            categoryId: state.categoryId,
            blueprintId: this.blueprintId,
            flashcardId: state.flashcardId,
          },
          query: this.currentQuery,
        };
      } else {
        // Multi-section simulation (sims#sectionResults)
        return {
          name: 'sims#sectionResults#question',
          params: {
            categoryId: this.categoryId,
            sectionId: state.categoryId,
            flashcardId: state.flashcardId,
          },
          query: this.currentQuery,
        };
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  .results-component-container {
    background: #EDF1F5;
    @include full-padding;
  }
</style>
